const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
  'ul',
]);

const VOID_TAGS = new Set(['br', 'hr']);
const SAFE_CLASS = /^[a-z0-9_\-\s]+$/i;
const SAFE_ATTR_VALUE = /^[^<>"`]+$/;
const SAFE_STYLE_VALUE = /^[#(),.%\sa-z0-9-]+$/i;
const ALLOWED_STYLES = new Set(['color', 'background-color', 'text-align']);

function escapeAttr(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function isSafeUrl(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (raw.startsWith('#') || raw.startsWith('/') || raw.startsWith('./') || raw.startsWith('../')) return true;
  return /^(https?:|mailto:|tel:)/i.test(raw);
}

function sanitizeStyle(value = '') {
  const safeRules = String(value || '')
    .split(';')
    .map((rule) => {
      const idx = rule.indexOf(':');
      if (idx < 0) return '';

      const prop = rule.slice(0, idx).trim().toLowerCase();
      const val = rule.slice(idx + 1).trim();
      if (!ALLOWED_STYLES.has(prop)) return '';
      if (!SAFE_STYLE_VALUE.test(val)) return '';
      if (/url|expression|javascript|behavior/i.test(val)) return '';
      return `${prop}: ${val}`;
    })
    .filter(Boolean);

  return safeRules.join('; ');
}

function sanitizeAttributes(tag, rawAttrs = '') {
  const attrs = [];
  const attrRegex = /([a-z0-9:-]+)(?:\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/gi;
  let match;

  while ((match = attrRegex.exec(rawAttrs)) !== null) {
    const name = String(match[1] || '').toLowerCase();
    const value = match[3] ?? match[4] ?? match[5] ?? '';

    if (!name || name.startsWith('on')) continue;

    if (name === 'class') {
      if (SAFE_CLASS.test(value)) attrs.push(`class="${escapeAttr(value.trim())}"`);
      continue;
    }

    if (name === 'style') {
      const style = sanitizeStyle(value);
      if (style) attrs.push(`style="${escapeAttr(style)}"`);
      continue;
    }

    if (tag === 'li' && name === 'data-list') {
      if (['ordered', 'bullet', 'checked', 'unchecked'].includes(String(value).trim().toLowerCase())) {
        attrs.push(`data-list="${escapeAttr(String(value).trim().toLowerCase())}"`);
      }
      continue;
    }

    if ((tag === 'ul' || tag === 'ol') && name === 'data-checked') {
      if (value === 'true' || value === 'false') {
        attrs.push(`data-checked="${value}"`);
      }
      continue;
    }

    if (tag === 'a' && name === 'href') {
      if (isSafeUrl(value) && SAFE_ATTR_VALUE.test(value)) {
        attrs.push(`href="${escapeAttr(value.trim())}"`);
      }
      continue;
    }

    if (tag === 'a' && name === 'target') {
      if (value === '_blank' || value === '_self') attrs.push(`target="${value}"`);
      continue;
    }

    if (tag === 'a' && name === 'rel') {
      if (SAFE_CLASS.test(value)) attrs.push(`rel="${escapeAttr(value.trim())}"`);
    }
  }

  if (tag === 'a' && attrs.some((attr) => attr.startsWith('target="_blank"')) && !attrs.some((attr) => attr.startsWith('rel='))) {
    attrs.push('rel="noopener noreferrer"');
  }

  return attrs.length > 0 ? ` ${attrs.join(' ')}` : '';
}

export function sanitizeHtml(value) {
  if (typeof value !== 'string' || value.length === 0) return value;

  return value
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<!doctype[^>]*>/gi, '')
    .replace(/<\s*(script|style|iframe|object|embed|svg|math|link|meta|base|form|input|button|textarea|select)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*\/?\s*(script|style|iframe|object|embed|svg|math|link|meta|base|form|input|button|textarea|select)[^>]*>/gi, '')
    // Início de tag HTML é `<` seguido, SEM espaço, de `/` opcional e de uma LETRA. `< 10 & margem >`, `<10` e `a < b` são texto, não tag: a regra anterior
    // (`\s*` depois do `<` e nome iniciado por dígito) apagava o trecho entre `<` e `>` de textos simples (ex.: short_description de uma Offer).
    .replace(/<(\/?)([a-z][a-z0-9-]*)([^>]*)>/gi, (full, closing, rawTag, rawAttrs) => {
      const tag = String(rawTag || '').toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) return '';

      if (closing) {
        return VOID_TAGS.has(tag) ? '' : `</${tag}>`;
      }

      const attrs = sanitizeAttributes(tag, rawAttrs);
      return VOID_TAGS.has(tag) ? `<${tag}${attrs}>` : `<${tag}${attrs}>`;
    });
}

export function sanitizeHtmlFields(value) {
  if (typeof value === 'string') return sanitizeHtml(value);
  if (Array.isArray(value)) return value.map((item) => sanitizeHtmlFields(item));
  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, sanitizeHtmlFields(item)])
  );
}
